import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Field } from './Input';
import { Spinner } from './Spinner';

export interface ComboboxOption {
  id: number;
  label: string;
  sublabel?: string;
}

interface ComboboxProps {
  value: number | null;
  onChange: (id: number | null, option: ComboboxOption | null) => void;
  /** Called as the user types; returns the suggestions to show. */
  onSearch: (term: string) => Promise<ComboboxOption[]>;
  label?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  /** Shows the current selection's label when the component mounts. */
  initialLabel?: string;
  clearable?: boolean;
}

/**
 * Typeahead picker — the "type and get suggestions" control the requirements
 * ask for on the invoice, mapping and collection screens.
 *
 * Deliberately not a plain <select>: the shop list runs to hundreds of names
 * that are similar to each other, and scrolling a dropdown to find
 * "Damayanthi Barampola" is slower and more error-prone than typing three
 * letters.
 */
export function Combobox({
  value,
  onChange,
  onSearch,
  label,
  error,
  hint,
  required,
  placeholder,
  disabled,
  initialLabel,
  clearable = true,
}: ComboboxProps): JSX.Element {
  const { t } = useTranslation();
  const id = useId();
  const containerRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [options, setOptions] = useState<ComboboxOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const [selectedLabel, setSelectedLabel] = useState(initialLabel ?? '');

  useEffect(() => {
    if (initialLabel !== undefined) setSelectedLabel(initialLabel);
  }, [initialLabel]);

  // Debounced search. The 250ms keeps a fast typist from firing a request per
  // keystroke; the cancelled flag stops a slow earlier response from
  // overwriting a faster later one.
  useEffect(() => {
    if (!isOpen) return undefined;

    let cancelled = false;
    setIsLoading(true);

    const timer = setTimeout(() => {
      void onSearch(term)
        .then((results) => {
          if (cancelled) return;
          setOptions(results);
          setHighlighted(0);
        })
        .finally(() => {
          if (!cancelled) setIsLoading(false);
        });
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term, isOpen]);

  // Close on outside click.
  useEffect(() => {
    if (!isOpen) return undefined;

    const handler = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
        setTerm('');
      }
    };

    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  const select = (option: ComboboxOption) => {
    onChange(option.id, option);
    setSelectedLabel(option.label);
    setIsOpen(false);
    setTerm('');
  };

  const clear = () => {
    onChange(null, null);
    setSelectedLabel('');
    setTerm('');
  };

  return (
    <Field label={label} error={error} hint={hint} required={required} htmlFor={id}>
      <div ref={containerRef} className="relative">
        <div className="relative">
          <input
            id={id}
            type="text"
            role="combobox"
            aria-expanded={isOpen}
            aria-controls={`${id}-listbox`}
            aria-autocomplete="list"
            autoComplete="off"
            disabled={disabled}
            placeholder={placeholder ?? t('common.searchPlaceholder')}
            value={isOpen ? term : selectedLabel}
            onFocus={() => setIsOpen(true)}
            onChange={(event) => {
              setTerm(event.target.value);
              if (!isOpen) setIsOpen(true);
            }}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown') {
                event.preventDefault();
                setIsOpen(true);
                setHighlighted((index) => Math.min(index + 1, options.length - 1));
              } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                setHighlighted((index) => Math.max(index - 1, 0));
              } else if (event.key === 'Enter') {
                const option = options[highlighted];
                if (isOpen && option) {
                  event.preventDefault();
                  select(option);
                }
              } else if (event.key === 'Escape') {
                setIsOpen(false);
                setTerm('');
              }
            }}
            className={cn(
              'h-10 w-full rounded-lg border bg-white pl-3 pr-16 text-sm text-slate-900',
              'placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30',
              'disabled:bg-slate-50 disabled:text-slate-500',
              error ? 'border-red-400' : 'border-slate-300',
            )}
          />

          <div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1">
            {isLoading && <Spinner className="text-slate-400" />}
            {clearable && value !== null && !disabled && (
              <button
                type="button"
                onClick={clear}
                className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                aria-label={t('common.clear')}
              >
                <X className="h-4 w-4" />
              </button>
            )}
            <ChevronDown className="h-4 w-4 text-slate-400" aria-hidden />
          </div>
        </div>

        {isOpen && (
          <ul
            id={`${id}-listbox`}
            role="listbox"
            className="absolute z-30 mt-1 max-h-64 w-full overflow-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
          >
            {options.length === 0 && !isLoading && (
              <li className="px-3 py-2 text-sm text-slate-500">{t('common.noResults')}</li>
            )}

            {options.map((option, index) => (
              <li key={option.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={option.id === value}
                  onMouseEnter={() => setHighlighted(index)}
                  onClick={() => select(option)}
                  className={cn(
                    'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm',
                    index === highlighted ? 'bg-brand-50 text-brand-900' : 'text-slate-700',
                  )}
                >
                  <span className="min-w-0">
                    <span className="block truncate font-medium">{option.label}</span>
                    {option.sublabel && (
                      <span className="block truncate text-xs text-slate-500">{option.sublabel}</span>
                    )}
                  </span>
                  {option.id === value && <Check className="h-4 w-4 shrink-0 text-brand-600" />}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Field>
  );
}
